import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { Capture, type CreateCaptureBody } from '@klunkers/shared'
import { api, ApiRequestError, NetworkError } from '@/api/http'
import { attachAudio, imageType, presign, putObject, recordPhoto } from '@/api/uploads'
import { blobToDataUrl, dataUrlToBlob, shrinkImage } from '@/lib/images'
import { importNativeNotes } from '@/capture/nativeInbox'
import * as queue from '@/capture/queue'
import { onResume } from '@/native/appEvents'
import { listNativeNotes, removeNativeNotes } from '@/native/captureInbox'
import { localStore } from '@/native/storage'
import { audioType, type Recording } from '@/native/voice'
import { useAuthStore } from './auth'

const RETRY_MIN_MS = 5_000
const RETRY_MAX_MS = 5 * 60_000

function failure(err: unknown): { ok: false; retry: boolean; message: string } {
  if (err instanceof NetworkError) return { ok: false, retry: true, message: err.message }
  if (err instanceof ApiRequestError) {
    // Signed out, rate limited or a server fault: try again later.
    const retry = err.status === 401 || err.status === 429 || err.status >= 500
    return { ok: false, retry, message: err.message }
  }
  return {
    ok: false,
    retry: true,
    message: err instanceof Error ? err.message : 'Unexpected error',
  }
}

async function send(body: queue.QueuedCapture['body']): Promise<queue.CaptureSendResult> {
  try {
    const capture = await api('/captures', { method: 'POST', body, schema: Capture })
    return { ok: true, captureId: capture.id }
  } catch (err) {
    return failure(err)
  }
}

async function sendPhoto(photo: queue.QueuedPhoto, captureId: string): Promise<queue.SendResult> {
  try {
    const blob = await dataUrlToBlob(photo.data_url)
    const type = imageType(photo.content_type)
    const signed = await presign(type, blob.size, photo.upload_id)
    await putObject(signed.upload_url, signed.headers, blob)
    await recordPhoto({
      object_key: signed.object_key,
      content_type: type,
      owner_type: 'capture',
      owner_id: captureId,
    })
    return { ok: true }
  } catch (err) {
    return failure(err)
  }
}

async function sendAudio(audio: queue.QueuedAudio, captureId: string): Promise<queue.SendResult> {
  try {
    const blob = await dataUrlToBlob(audio.data_url)
    const type = audioType(audio.content_type)
    const signed = await presign(type, blob.size, audio.upload_id)
    await putObject(signed.upload_url, signed.headers, blob)
    await attachAudio(captureId, {
      object_key: signed.object_key,
      content_type: type,
      duration_s: audio.duration_s,
    })
    return { ok: true }
  } catch (err) {
    return failure(err)
  }
}

/**
 * Captures waiting to reach the server. `save` never waits for the network;
 * syncing happens in the background, on start, when the device comes back
 * online, and on a backing-off timer.
 */
export const useCaptureQueueStore = defineStore('captureQueue', () => {
  const pending = ref<queue.QueuedCapture[]>([])
  const syncing = ref(false)
  const lastSyncedAt = ref<string | null>(null)
  let timer: ReturnType<typeof setTimeout> | null = null
  let delay = RETRY_MIN_MS
  let running: Promise<void> | null = null
  const deps = (): queue.QueueDeps => ({ store: localStore(), send, sendPhoto, sendAudio })

  const waiting = computed(() => pending.value.filter((p) => !p.rejected))
  const rejected = computed(() => pending.value.filter((p) => p.rejected))

  function userId(): string | null {
    return useAuthStore().userId
  }

  async function refresh() {
    const id = userId()
    pending.value = id ? await queue.list(deps(), id).catch(() => []) : []
  }

  /** Write the capture (and photo or voice note) to the device and return. No network in this path. */
  async function save(
    body: Omit<CreateCaptureBody, 'client_id' | 'captured_at' | 'has_audio'> & {
      captured_at?: string
    },
    photo?: Blob,
    voice?: Recording,
  ) {
    const id = userId()
    if (!id) throw new Error('Sign in to save captures.')
    // Local work only: shrink and encode the photo on the device.
    const queuedPhoto = photo
      ? await shrinkImage(photo).then(async (small) => ({
          upload_id: crypto.randomUUID(),
          content_type: small.type || 'image/jpeg',
          data_url: await blobToDataUrl(small),
        }))
      : undefined
    const queuedAudio = voice
      ? {
          upload_id: crypto.randomUUID(),
          content_type: voice.contentType,
          duration_s: voice.durationS,
          data_url: await blobToDataUrl(voice.blob),
        }
      : undefined
    const item = await queue.enqueue(
      deps(),
      id,
      {
        ...body,
        client_id: crypto.randomUUID(),
        captured_at: body.captured_at ?? new Date().toISOString(),
        has_audio: !!queuedAudio,
      },
      queuedPhoto,
      queuedAudio,
    )
    pending.value = [...pending.value, item]
    void syncNow()
    return item
  }

  /**
   * Bring in notes made from a widget, tile or shortcut while the app was
   * closed. Local only; the normal sync sends them.
   */
  async function importFromDevice(): Promise<number> {
    const id = userId()
    if (!id) return 0
    try {
      const count = await importNativeNotes(
        { list: listNativeNotes, remove: removeNativeNotes, store: localStore() },
        id,
      )
      if (count) await refresh()
      return count
    } catch {
      return 0
    }
  }

  /**
   * Set to ask the capture button to open the sheet (deep links). It stays
   * set until the button, which may not be mounted yet, takes it.
   */
  const sheetPending = ref(false)
  function requestSheet() {
    sheetPending.value = true
  }

  function schedule(ms: number) {
    if (timer) clearTimeout(timer)
    timer = setTimeout(() => void syncNow(), ms)
  }

  /** Replay the queue. Safe to call often; concurrent calls share one run. */
  function syncNow(): Promise<void> {
    running ??= (async () => {
      const id = userId()
      if (!id) return
      syncing.value = true
      try {
        const outcome = await queue.sync(deps(), id)
        if (outcome.sent) lastSyncedAt.value = new Date().toISOString()
        if (outcome.interrupted) {
          schedule(delay)
          delay = Math.min(delay * 2, RETRY_MAX_MS)
        } else {
          delay = RETRY_MIN_MS
        }
      } catch {
        schedule(delay)
      } finally {
        syncing.value = false
        await refresh()
      }
    })().finally(() => {
      running = null
    })
    return running
  }

  async function discard(item: queue.QueuedCapture) {
    await queue.discard(deps(), item)
    await refresh()
  }

  let started = false
  /** Start background syncing. Called once the rider is signed in. */
  function start() {
    if (started) return
    started = true
    window.addEventListener('online', () => {
      delay = RETRY_MIN_MS
      void syncNow()
    })
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') void syncNow()
    })
    void onResume(() => void importFromDevice().then(syncNow))
    void importFromDevice().then(refresh).then(syncNow)
  }

  return {
    pending,
    waiting,
    rejected,
    syncing,
    lastSyncedAt,
    save,
    syncNow,
    refresh,
    discard,
    start,
    importFromDevice,
    sheetPending,
    requestSheet,
  }
})
